/**
 * Every number the Director uses, in one place.
 *
 * The decision this file encodes is D8 (timers) and D10/D11 (motion) in
 * `OVERHAUL-PLAN-2026-09-05.md`: bots think for two to four seconds, longer in a big
 * spot, up to about six; a human gets ten quiet seconds and then a five-second hurry-up;
 * the motion durations are the Normal pace, and Fast halves them while Cinematic takes
 * half again. Nothing in a component may carry a duration of its own — a component that
 * knows how long a chip takes to slide is a component with an opinion about pace, and
 * pace is a setting.
 *
 * All durations are milliseconds. The human clock does NOT scale with pace: ten plus
 * five seconds is a rule about fairness, not about how the table feels, and a player
 * who picks Fast has asked the bots to hurry, not to be rushed themselves.
 */

export type Pace = "fast" | "normal" | "cinematic";

/** The multiplier each pace applies to the Normal durations below. */
export const PACE_SCALE: Record<Pace, number> = { fast: 0.5, normal: 1, cinematic: 1.5 };

/**
 * The Normal durations, one per animated event, straight from the plan.
 *
 * `dealStagger` is the gap between one card leaving the dealer and the next; `deal` is
 * how long each card is in the air. Stagger is shorter than flight, so at any moment two
 * cards are travelling — which is what a real deal looks like from above.
 */
export const MOTION = {
  shuffle: 900,
  deal: 120,
  dealStagger: 80,
  betSlide: 350,
  potCollect: 450,
  flip: 300,
  /** Board cards flip in one after another; this is the gap between them. */
  flipStagger: 100,
  potPush: 600,
  buttonHop: 300,
  /** A check has no chip to move; the table still needs a beat to read it. */
  actionBeat: 250,
  /** A fold: the seat's two cards slide to the muck in the middle and fade there. */
  muck: 300,
  /**
   * Suspense. When the chips are all in and the hands are on their backs, the run-out
   * is the best moment in the game and it must not be rushed: a held breath before the
   * flop and the turn, a longer one before the river, and a beat after the last card
   * before the pot moves. A normal showdown gets the same beat between the hands
   * turning over and the pot being pushed, so the winner can be read.
   */
  runoutPause: 1500,
  riverPause: 2000,
  showdownPause: 1000,
  /** The pause between `hand_end` and the next deal, when auto-deal is on. */
  betweenHands: 1600,

  /* ---- Phase 3b: the character of the motion ---- */
  /** Anything that lands settles: scale 1.05 → 1 over this, after the flight. */
  settle: 60,
  /** Collect: each seat's pile leaves this much after the previous, clockwise from the button. */
  collectStagger: 40,
  /** A side pot leaves this long after the main pot has landed. */
  sidePotGap: 150,
  /** The two mucked cards leave this far apart. */
  muckStagger: 40,
  /** The shuffle's parts: the halves part, riffle three times, square up. Sums to `shuffle`. */
  shuffleSplit: 180,
  riffle: 120,
  shuffleSquare: 360,
  /** The payoff: the winner's ring pulses twice over this, and the result line holds at least this long. */
  winPulse: 600,
  winHold: 1000,
  /** Images are waited for before the first hand is shown — but never longer than this. */
  preloadCeiling: 3000,

  /* ---- Phase 6: the table's voice ---- */
  /**
   * The bubble: in fast, hold, out slower.
   *
   * A bubble that fades in slowly reads as an animation; one that fades OUT quickly
   * reads as a glitch. 120 in and 200 out is the shape the plan asks for, and the hold
   * between them is the two seconds §6 decided — which scales with pace like every
   * other duration here, because a player who chose Cinematic asked for longer beats
   * and a player who chose Fast asked for shorter ones.
   */
  tauntIn: 120,
  tauntHold: 2000,
  tauntOut: 200,
  /** The avatar's face, held whether or not a bubble came with it. */
  expressionHold: 2000,
  /**
   * How long a shown hand stays face up on the felt, and how long the hero is offered
   * the choice. Three seconds: long enough to read two cards across a table, short
   * enough that it is over before the next shuffle wants the felt back.
   */
  showHold: 3000,
} as const;

export type MotionKey = keyof typeof MOTION;

/**
 * The SHAPE of each flight — the fractions the keyframes are built from. They are not
 * durations, so they do not scale with pace; they live here so that no component
 * carries a number of its own.
 */
export const SHAPE = {
  /** A bet's arc: mid-flight lift as a fraction of the distance travelled. */
  betArc: 0.06,
  /** A bet's swell at mid-flight. */
  betScale: 1.15,
  /** The pot's swell on its way to the winner. */
  pushScale: 1.2,
  /** A dealt card starts at this scale and grows to 1. */
  dealScale: 0.9,
  /** The hero's cards fly at seat size and grow to hero size over this last fraction. */
  dealGrowFrom: 0.7,
  /** The settle after a landing: from this scale down to 1. */
  settleScale: 1.05,
  /** The muck: fade over this last fraction of the flight; end at this scale. */
  muckFadeFrom: 0.5,
  muckEndScale: 0.85,
  /** The disc's hop: lift at the midpoint as a fraction of its height. */
  hopLift: 0.2,
  /** The shuffle: how far the halves part, as a fraction of a card's width. */
  shuffleSplit: 0.3,
  /** The winner's cards lift by this fraction of their height; a loser's dim to this opacity. */
  winLift: 0.08,
  loserDim: 0.7,
  /** Cards in the deck are offset by this many pixels each in x and y. */
  deckOffsetPx: 1,
  deckCards: 5,
  deckStub: 2,
} as const;

/**
 * How long a bot takes over a decision, before any spot modifier.
 *
 * Drawn uniformly from `[min, max]` by the driver's own RNG, then stretched by the
 * spot: bigger pot, later street, or an all-in to face each add to it, capped at `cap`.
 * The engine's own compute — Monte-Carlo pricing, up to a few hundred milliseconds —
 * happens before the timeline reaches the action, so it hides inside the delay.
 */
export const THINK = {
  min: 2000,
  max: 4000,
  cap: 6000,
  /** Added per street after preflop: a river decision is slower than an open. */
  perStreet: 300,
  /** Added when the seat is facing an all-in bet. */
  facingAllIn: 1000,
  /** Added per ten big blinds in the pot, so a big pot reads as a big decision. */
  perTenBigBlinds: 250,
  /** Bots never "think" for less than this whatever the pace, or the table looks broken. */
  floor: 700,
} as const;

/** The human clock. `quiet` is the gold ring; `hurry` the oxblood one with the numeral. */
export const HUMAN_CLOCK = {
  quiet: 10_000,
  hurry: 5_000,
  /** Turbo tables, later: the whole clock, quiet plus hurry. Not wired in this phase. */
  turboTotal: 15_000,
} as const;

/** Scale a Normal duration to a pace. */
export function scaled(key: MotionKey, pace: Pace): number {
  return Math.round(MOTION[key] * PACE_SCALE[pace]);
}

/**
 * Everything the timeline needs for one pace, resolved once so the components never
 * see a multiplier. `reduced` is `prefers-reduced-motion`: Fast, and every flight
 * becomes a fade at its destination — the durations are kept so the rhythm survives,
 * because a table where everything happens at once is not calmer, it is illegible.
 */
export interface Durations {
  pace: Pace;
  reduced: boolean;
  shuffle: number; deal: number; dealStagger: number; betSlide: number;
  potCollect: number; flip: number; flipStagger: number; potPush: number;
  buttonHop: number; actionBeat: number; muck: number; betweenHands: number;
  runoutPause: number; riverPause: number; showdownPause: number;
  settle: number; collectStagger: number; sidePotGap: number; muckStagger: number;
  shuffleSplit: number; riffle: number; shuffleSquare: number;
  winPulse: number; winHold: number; preloadCeiling: number;
  /* Phase 6. */
  tauntIn: number; tauntHold: number; tauntOut: number;
  expressionHold: number; showHold: number;
}

export function durationsFor(pace: Pace, reduced = false): Durations {
  const p: Pace = reduced ? "fast" : pace;
  return {
    pace: p, reduced,
    shuffle: scaled("shuffle", p), deal: scaled("deal", p), dealStagger: scaled("dealStagger", p),
    betSlide: scaled("betSlide", p), potCollect: scaled("potCollect", p), flip: scaled("flip", p),
    flipStagger: scaled("flipStagger", p), potPush: scaled("potPush", p),
    buttonHop: scaled("buttonHop", p), actionBeat: scaled("actionBeat", p),
    muck: scaled("muck", p), betweenHands: scaled("betweenHands", p),
    runoutPause: scaled("runoutPause", p), riverPause: scaled("riverPause", p),
    showdownPause: scaled("showdownPause", p),
    settle: scaled("settle", p), collectStagger: scaled("collectStagger", p),
    sidePotGap: scaled("sidePotGap", p), muckStagger: scaled("muckStagger", p),
    shuffleSplit: scaled("shuffleSplit", p), riffle: scaled("riffle", p), shuffleSquare: scaled("shuffleSquare", p),
    winPulse: scaled("winPulse", p), winHold: scaled("winHold", p),
    /* The ceiling is a promise about the player's time, not about feel: it does not scale. */
    preloadCeiling: MOTION.preloadCeiling,
    tauntIn: scaled("tauntIn", p), tauntHold: scaled("tauntHold", p), tauntOut: scaled("tauntOut", p),
    expressionHold: scaled("expressionHold", p), showHold: scaled("showHold", p),
  };
}

/** What the timeline knows about a spot when it picks a bot's think time. */
export interface Spot {
  street: "preflop" | "flop" | "turn" | "river";
  /** Pot in big blinds, including what is in front of the seats. */
  potBb: number;
  facingAllIn: boolean;
}

const STREET_INDEX = { preflop: 0, flop: 1, turn: 2, river: 3 } as const;

/**
 * A bot's think time for one spot, in milliseconds at the given pace.
 *
 * `u` is a uniform draw in [0, 1) from the DRIVER's RNG — never the engine's. The base
 * is uniform in the band; the modifiers stretch it; the cap holds; then the pace
 * scales it and the floor catches a Fast table so a bot never appears to act before
 * the previous chip has landed.
 */
export function thinkTime(u: number, spot: Spot, pace: Pace): number {
  const base = THINK.min + u * (THINK.max - THINK.min);
  const stretch = STREET_INDEX[spot.street] * THINK.perStreet
    + (spot.facingAllIn ? THINK.facingAllIn : 0)
    + Math.min(4, spot.potBb / 10) * THINK.perTenBigBlinds;
  const ms = Math.min(THINK.cap, base + stretch) * PACE_SCALE[pace];
  return Math.max(THINK.floor, Math.round(ms));
}

/* ====================================================== PHASE 8: BLACKJACK ====== */

/**
 * The blackjack table's numbers, beside poker's and never instead of them.
 *
 * A blackjack decision is quicker than a poker one — there is no pot to weigh and the
 * chart is in everybody's head — so a seat thinks for ONE TO TWO seconds, half a second
 * more when it splits or doubles (the chips have to be counted out), scaled by pace like
 * every duration here. The deal, the flip, the chip slide and the payout are poker's own
 * numbers (`MOTION`), because a card leaving a shoe is the same card leaving a deck.
 * The one beat that is blackjack's alone is the breath before the hole card turns.
 */
export const BJ_THINK = {
  min: 1000,
  max: 2000,
  /** Added on a split or a double. */
  bigger: 500,
  /** Never less than this, whatever the pace, or the seat looks as if it did not think. */
  floor: 400,
} as const;

export const BJ_MOTION = {
  /** "A 600 ms beat before the hole card turns" (the brief). */
  holeBeat: 600,
  /** The dealer's cards come one at a time; the gap between them. */
  dealerDraw: 450,
  /** A settled hand's chips move one hand after another; the gap between them. */
  settleStagger: 150,
} as const;

/** The blackjack durations at a pace: poker's resolved set plus the three above. */
export interface BjDurations extends Durations {
  holeBeat: number; dealerDraw: number; settleStagger: number;
}

export function bjDurationsFor(pace: Pace, reduced = false): BjDurations {
  const d = durationsFor(pace, reduced);
  const k = PACE_SCALE[d.pace];
  return {
    ...d,
    holeBeat: Math.round(BJ_MOTION.holeBeat * k),
    dealerDraw: Math.round(BJ_MOTION.dealerDraw * k),
    settleStagger: Math.round(BJ_MOTION.settleStagger * k),
  };
}

/**
 * A blackjack seat's think time, in milliseconds at a pace. `u` is a uniform draw from
 * the DRIVER's generator — never the engine's, which is what keeps a driven sitting the
 * same sitting as an undriven one (`blackjack-director.test.ts`).
 */
export function bjThinkTime(u: number, action: "hit" | "stand" | "double" | "split", pace: Pace): number {
  const base = BJ_THINK.min + u * (BJ_THINK.max - BJ_THINK.min)
    + (action === "double" || action === "split" ? BJ_THINK.bigger : 0);
  return Math.max(BJ_THINK.floor, Math.round(base * PACE_SCALE[pace]));
}
